import styled from "styled-components";
import { formatDatetime, formatEmail, getUserStation } from "../../../utils";
import { useAppContext } from "../../../context";
import ImageCard from "./ImageCard";
import { useMemo, useState } from "react";
import { generateDamageReport } from "../../../components/warehouse/damageReporting/utils";
import moment from "moment";
import Lightbox from "react-image-lightbox";
import useAppData from "../../../customHooks/useAppData";

export interface RenderImage {
    url: string;
    title: string;
    comments: string;
    collectionId: string;
    createdBy: string;
    createdAt: string;
}

export interface Collection {
    collectionId: string;
    title: string;
    createdBy: string;
    createdAt: string;
    comments: string;
    images: Array<RenderImage>;
}

export default function RenderImages({
    searchComplete,
    images,
    createdBy,
    createdAt,
    comments,
    reportType,
    title,
    collection_id,
    flightNumber,
    flightDate,
    type,
    deleteImage
}: {
    searchComplete: boolean,
    images: Array<RenderImage>;
    createdBy: string;
    createdAt: string;
    comments: string;
    reportType: 'DAMAGE' | 'RAMP';
    title: string;
    collection_id?: string;
    flightNumber?: string;
    flightDate?: string;
    type?: string;
    deleteImage?: (url: string) => Promise<void>;
}) {

    const { user, setLoading } = useAppContext();
    const { stations } = useAppData();
    const userStation = getUserStation(stations, user);

    const [lightBoxOpen, setLightBoxOpen] = useState(false);
    const [photoIndex, setPhotoIndex] = useState(0);

    const collections = useMemo(() => {
        const map: Record<string, Collection> = {};
        for (const item of images) {
            const { collectionId, createdBy, createdAt, comments } = item;
            if (!map[collectionId]) {
                map[collectionId] = {
                    title,
                    collectionId,
                    createdBy,
                    createdAt,
                    comments,
                    images: []
                };
            }
            map[collectionId].images.push(item);
        }
        return Object.keys(map).map(key => map[key]);
    }, [images, title]);


    const generateReport = (generateType: 'email' | 'print', collection: Collection) => {
        const imagesArray = collection.images.map(item => item.url);
        const { createdBy, createdAt, comments } = collection;
        generateDamageReport(
            generateType,
            user,
            userStation,
            createdBy,
            createdAt,
            comments,
            imagesArray,
            title,
            reportType,
            collection_id,
            flightNumber,
            flightDate,
            type,
            setLoading,
        );
    }

    const viewPhoto = (photoIndex: number) => {
        setPhotoIndex(photoIndex);
        setLightBoxOpen(true);
    }

    const handleDeleteImage = async (url: string) => {
        setLoading(true);
        await deleteImage(url);
        setLightBoxOpen(false);
        setLoading(false);
    }

    if (!searchComplete) return (
        <h6>Waiting for search</h6>
    );

    if (!images.length) {
        return (
            <h6>No results found</h6>
        );
    }

    return (
        <div>
            {
                collections.map((item, i) => (
                    <CollectionComponent
                        collection={item}
                        viewPhoto={viewPhoto}
                        generateReport={generateReport}
                        key={i}
                    />
                ))
            }
            {
                lightBoxOpen && (
                    // @ts-ignore
                    <Lightbox
                        mainSrc={images[photoIndex].url}
                        nextSrc={images[(photoIndex + 1) % images.length]}
                        prevSrc={
                            images[(photoIndex + images.length - 1) % images.length]
                        }
                        onCloseRequest={() => setLightBoxOpen(false)}
                        onMovePrevRequest={() =>
                            setPhotoIndex(
                                (photoIndex + images.length - 1) % images.length
                            )
                        }
                        onMoveNextRequest={() =>
                            setPhotoIndex((photoIndex + 1) % images.length)
                        }
                        imageTitle={<h1>{images[photoIndex].title}</h1>}
                        imageCaption={(
                            <ImageCaption
                                title={images[photoIndex].title}
                                comments={images[photoIndex].comments}
                                createdBy={images[photoIndex].createdBy}
                                createdAt={images[photoIndex].createdAt}
                                url={images[photoIndex].url}
                                handleDeleteImage={deleteImage ? handleDeleteImage : undefined}
                            />
                        )}
                    />
                )
            }
        </div >
    );
}

const CollectionComponent = ({
    collection,
    viewPhoto,
    generateReport
}: {
    collection: Collection;
    viewPhoto: (photoIndex: number) => void;
    generateReport: (generateType: "email" | "print", collection: Collection) => void;
}) => {

    const { createdBy, createdAt, images } = collection;

    return (
        <CollectionContainer>
            <SpaceBetween>
                <h6>Created by {formatEmail(createdBy)} at {formatDatetime(createdAt)}</h6>
                <FlexContainer>
                <i
                    style={{ fontSize: '48px' }}
                    className="far fa-envelope visual-caption-icon text-warning"
                    onClick={() => generateReport('email', collection)}
                ></i>
                <i
                    style={{ fontSize: '48px' }}
                    className="fas fa-cloud-download-alt visual-caption-icon text-primary"
                    onClick={() => generateReport('print', collection)}
                ></i>
            </FlexContainer>
            </SpaceBetween>
            <ImagesContainer>
                {
                    images.map((item, i) => (
                        <ImageCard
                            src={item.url}
                            index={i}
                            viewPhoto={viewPhoto}
                            key={i}
                        />
                    ))
                }
            </ImagesContainer>
        </CollectionContainer>
    );
}

const ImageCaption = ({
    title,
    comments,
    createdBy,
    createdAt,
    url,
    handleDeleteImage
}: {
    title: string;
    comments: string;
    createdBy: string;
    createdAt: string;
    url: string;
    handleDeleteImage?: (url: string) => Promise<void>;
}) => {
    return (
        <div className="row" style={{ width: '100%' }}>
            <div className="col-12" style={{ width: '100%' }}>
                <h1>
                    {title}: {comments}.{' '}
                    Reported by {formatEmail(createdBy)} at{' '}
                    {moment(createdAt).format(
                        'MM/DD/YYYY HH:mm'
                    )}
                </h1>
            </div>
            <ImageCaptionIconsContainer>
                {/* <div className="bg-warning text-center">
                    <i
                        style={{ fontSize: '48px' }}
                        className="far fa-envelope visual-caption-icon"
                        onClick={() => generateReport('email')}
                    ></i>
                </div>
                <div className="bg-secondary text-center">
                    <i
                        style={{ fontSize: '48px' }}
                        className="fas fa-cloud-download-alt visual-caption-icon"
                        onClick={() => generateReport('print')}
                    ></i>
                </div> */}
                {
                    Boolean(handleDeleteImage) !== false && (
                        <div className="bg-danger text-center">
                            <i
                                style={{ fontSize: '40px' }}
                                className="fas fa-trash visual-caption-icon"
                                onClick={() => handleDeleteImage(url)}
                            ></i>
                        </div>
                    )
                }
            </ImageCaptionIconsContainer>
        </div>
    );
}

const FlexContainer = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 20px;
`;

const SpaceBetween = styled.div`
    display: flex;
    justify-content: space-between;
`;

const ImagesContainer = styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
`;

const ImageCaptionIconsContainer = styled.div`
    display: flex;
    width: 100%;

    & div {
        flex: 1;
    }
`;

const CollectionContainer = styled.div`
    margin-bottom: 10px;
    border: 3px solid lightgray;
    border-radius: 10px;
    padding: 10px;
    background-color: lightgray;
`;